import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ActionCard, DataList, Field, SelectField, StatusPill, TextAreaField, type DataListColumn } from "@ozpanel/kit";
import { Button, ConfirmDialog, Section, StateView, formatRelative, useAuth, useToast } from "@ozpanel/ui";

// The tokens module's own page: EVERY access token on the platform (mail SSO / app passwords and
// service tokens, internal/accesstoken), not the administrator's own API keys (Security) and not
// the account token queue (Token requests).
//
// POST /tokens and POST /tokens/{jti}/revoke are the operator's issue and revoke for ANY subject. They
// existed with no console (CLAUDE.md rule 8): a support case "my phone's mail token leaked" was a curl.

/** accesstoken.Row as GET /tokens serialises it. */
interface TokenRow {
  jti: string;
  subject: string;
  tenant_id?: number;
  level: string;
  scopes: string;
  label: string;
  created_by: string;
  created_at: string;
  expires_at?: string;
  revoked_at?: string;
  last_used_at?: string;
}

export function AccessTokensOps() {
  const { client } = useAuth();
  const qc = useQueryClient();
  const { notify } = useToast();
  const q = useQuery({
    queryKey: ["access-tokens"],
    queryFn: () => client.apiFetch<{ tokens: TokenRow[] | null }>("/tokens"),
    refetchInterval: 30000,
  });
  const [revoking, setRevoking] = React.useState<TokenRow | null>(null);
  const [busy, setBusy] = React.useState(false);

  const [subject, setSubject] = React.useState("");
  const [level, setLevel] = React.useState("mailbox");
  const [scopes, setScopes] = React.useState("mail:imap mail:smtp");
  const [label, setLabel] = React.useState("");
  const [expires, setExpires] = React.useState("");

  const revoke = async (t: TokenRow) => {
    setBusy(true);
    try {
      await client.apiFetch(`/tokens/${encodeURIComponent(t.jti)}/revoke`, { method: "POST" });
      notify({ kind: "success", title: `token ${t.label || t.jti} revoked`, body: "It is refused on its next use." });
      void qc.invalidateQueries({ queryKey: ["access-tokens"] });
    } catch (e) {
      notify({ kind: "error", title: "the token was not revoked", body: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
      setRevoking(null);
    }
  };

  const state = (t: TokenRow) =>
    t.revoked_at ? (
      <StatusPill tone="neutral">revoked</StatusPill>
    ) : t.expires_at && new Date(t.expires_at).getTime() < Date.now() ? (
      <StatusPill tone="neutral">expired</StatusPill>
    ) : (
      <StatusPill tone="ok">active</StatusPill>
    );
  const cols: DataListColumn<TokenRow>[] = [
    { key: "subject", label: "Subject", sortable: true, value: (t) => t.subject, render: (t) => <span className="font-mono text-[12px]">{t.subject}</span> },
    { key: "label", label: "Label", value: (t) => t.label },
    { key: "level", label: "Level", sortable: true, value: (t) => t.level },
    { key: "scopes", label: "Scopes", value: (t) => t.scopes, render: (t) => <span className="font-mono text-[11.5px]">{t.scopes}</span> },
    { key: "state", label: "State", value: (t) => (t.revoked_at ? "revoked" : "active"), render: state },
    { key: "last_used_at", label: "Last used", sortable: true, value: (t) => t.last_used_at ?? "", render: (t) => (t.last_used_at ? formatRelative(t.last_used_at) : "never") },
    { key: "created_by", label: "Issued by", value: (t) => t.created_by },
  ];

  const expiresRFC = expires.trim() ? new Date(expires).toISOString().replace(/\.\d{3}Z$/, "Z") : "";
  const expiryValid = !expires.trim() || !Number.isNaN(new Date(expires).getTime());

  return (
    <div className="space-y-4">
      <Section title="Every access token" hint={q.data ? `${(q.data.tokens ?? []).length}` : undefined} bodyClassName="p-0">
        <StateView loading={q.isLoading} error={q.error} errorContext="the access tokens">
          <div className="p-4">
            <DataList
              items={q.data?.tokens ?? []}
              columns={cols}
              getRowKey={(t) => t.jti}
              searchable
              searchPlaceholder="Search subject, label…"
              rowActions={(t) =>
                t.revoked_at ? null : (
                  <Button size="sm" variant="ghost" disabled={busy} onClick={() => setRevoking(t)}>
                    Revoke
                  </Button>
                )
              }
              emptyMessage="No access tokens have been issued."
            />
          </div>
        </StateView>
      </Section>

      <Section title="Issue a token for any subject">
        <div className="space-y-3">
          <Field label="Subject" value={subject} onChange={setSubject} mono placeholder="user@example.com" help="A mailbox address for a mailbox token; a service name for a service token." />
          <SelectField
            label="Level"
            value={level}
            onChange={setLevel}
            options={[
              { value: "mailbox", label: "mailbox", help: "logs in to IMAP/SMTP as the subject" },
              { value: "service", label: "service", help: "a machine caller, no mailbox" },
            ]}
          />
          <TextAreaField label="Scopes" value={scopes} onChange={setScopes} mono rows={2} help="Space-separated, e.g. mail:imap mail:smtp." />
          <Field label="Label" value={label} onChange={setLabel} placeholder="what it is for" />
          <Field label="Expires (optional)" value={expires} onChange={setExpires} mono placeholder="2027-01-01T00:00:00Z" help="Empty = never. Any date the browser can read; sent as RFC 3339 UTC." />
          <ActionCard
            title="Issue the token"
            description="Mints a signed token for the subject with exactly these scopes. The token is shown in the list above, and works at once on every node."
            actionLabel="Issue"
            danger="medium"
            unavailable={!subject.trim() ? "A subject is required." : !expiryValid ? "The expiry is not a date." : undefined}
            confirm={{
              title: `Issue a ${level} token for ${subject.trim()}?`,
              body: "Whoever holds it can act as that subject within its scopes until it expires or is revoked here.",
              confirmLabel: "Issue",
            }}
            run={() =>
              client.apiFetch<{ token?: string }>("/tokens", {
                method: "POST",
                body: JSON.stringify({ subject: subject.trim(), level, scopes: scopes.trim(), label: label.trim(), expires_at: expiresRFC }),
              })
            }
            renderResult={(r) => {
              const tok = (r as { token?: string } | null)?.token;
              return tok ? <code className="break-all font-mono text-[12px]">{tok}</code> : "Issued.";
            }}
            invalidates={[["access-tokens"]]}
          />
        </div>
      </Section>

      <ConfirmDialog
        open={!!revoking}
        onOpenChange={(o) => !o && setRevoking(null)}
        title={`Revoke ${revoking?.label || revoking?.jti || "this token"}?`}
        destructive
        confirmLabel="Revoke"
        pending={busy}
        body={`Whatever uses it (${revoking?.subject ?? ""}) is refused on its next use and must be given a new token. This cannot be undone.`}
        onConfirm={() => {
          if (revoking) void revoke(revoking);
        }}
      />
    </div>
  );
}
